import { useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import AdminLayout from "@/components/layout-admin";
import {
  useOrgContracts,
  useOrgDataGrants,
  useContractSummary,
  useOrganizations,
  useApartments,
  useProposeContract,
  useAcceptContract,
  useRejectContract,
  useTerminateContract,
} from "@/hooks/use-resources";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ChevronLeft, Plus, AlertTriangle, Building2, CheckCircle2, Clock, FileText, DollarSign } from "lucide-react";
import type { OrgContract } from "@shared/schema";
import { serviceEngagementTermsSchema, permissionSchema } from "@shared/types/cross-org";
import type { Permission } from "@shared/types/cross-org";

// ─── Permission config ───────────────────────────────────────────────────────

const PERMISSION_GROUPS: { label: string; permissions: Permission[] }[] = [
  {
    label: "Apartment",
    permissions: [
      "apartment.read_details",
      "apartment.read_address",
      "apartment.read_access_info",
    ],
  },
  {
    label: "Cleaning",
    permissions: [
      "cleaning_job.read",
      "cleaning_job.complete",
      "cleaning_job.upload_photos",
      "cleaning_job.assign",
    ],
  },
  {
    label: "Maintenance",
    permissions: [
      "maintenance_job.read",
      "maintenance_job.complete",
      "maintenance_job.upload_photos",
      "maintenance_job.create",
    ],
  },
  {
    label: "Reservation",
    permissions: [
      "reservation.read_schedule",
      "reservation.read_contact",
      "reservation.read_pii",
    ],
  },
];

const CLEANING_ONLY: Permission[] = [
  "apartment.read_details",
  "apartment.read_address",
  "apartment.read_access_info",
  "cleaning_job.read",
  "cleaning_job.complete",
  "cleaning_job.upload_photos",
  "cleaning_job.assign",
];

const CLEANING_AND_MAINTENANCE: Permission[] = [
  ...CLEANING_ONLY,
  "maintenance_job.read",
  "maintenance_job.complete",
  "maintenance_job.upload_photos",
  "maintenance_job.create",
];

const FULL_OPERATIONAL: Permission[] = permissionSchema.options as Permission[];

const PRESETS: Record<string, Permission[]> = {
  "Cleaning only": CLEANING_ONLY,
  "Cleaning + Maintenance": CLEANING_AND_MAINTENANCE,
  "Full operational": FULL_OPERATIONAL,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    proposed: "bg-yellow-100 text-yellow-800",
    active: "bg-green-100 text-green-800",
    suspended: "bg-orange-100 text-orange-800",
    terminated: "bg-red-100 text-red-800",
    expired: "bg-gray-100 text-gray-800",
    rejected: "bg-red-100 text-red-700",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
        map[status] ?? "bg-gray-100 text-gray-700"
      }`}
    >
      {status}
    </span>
  );
}

// ─── ProposeContractDialog ────────────────────────────────────────────────────

const BEDROOM_LABELS: Record<string, string> = {
  "0": "Studio (0 bedrooms)",
  "1": "1 Bedroom",
  "2": "2 Bedrooms",
  "3": "3 Bedrooms",
  "4+": "4+ Bedrooms",
};

function ProposeContractDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { data: orgs = [] } = useOrganizations();
  const { data: apartments = [] } = useApartments();
  const propose = useProposeContract();

  // My role: 'provider' = I manage the apartments and grant access
  //          'consumer' = I'm the service org, I set my rates
  const [myRole, setMyRole] = useState<"provider" | "consumer">("provider");
  const [title, setTitle] = useState("");
  const [counterpartyOrgId, setCounterpartyOrgId] = useState<string>("");
  const [endsAt, setEndsAt] = useState("");
  const [cadence, setCadence] = useState<"weekly" | "biweekly" | "monthly">("monthly");
  // Provider fields
  const [selectedApts, setSelectedApts] = useState<number[]>([]);
  const [selectedPerms, setSelectedPerms] = useState<Permission[]>([]);
  // Service org fields — rate card per bedroom count
  const [rateCard, setRateCard] = useState<Record<string, string>>({ "0": "", "1": "", "2": "", "3": "", "4+": "" });
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setMyRole("provider");
    setTitle("");
    setCounterpartyOrgId("");
    setEndsAt("");
    setCadence("monthly");
    setSelectedApts([]);
    setSelectedPerms([]);
    setRateCard({ "0": "", "1": "", "2": "", "3": "", "4+": "" });
    setError(null);
  }

  function toggleApt(id: number) {
    setSelectedApts((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  function togglePerm(p: Permission) {
    setSelectedPerms((prev) =>
      prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
    );
  }

  async function handleSubmit() {
    setError(null);

    if (!title.trim()) return setError("Title is required.");
    if (!counterpartyOrgId) return setError("Select the other organization.");
    if (!endsAt) return setError("End date is required.");

    if (myRole === "provider" && selectedApts.length === 0) {
      return setError("Select at least one apartment to grant access to.");
    }
    if (myRole === "provider" && selectedPerms.length === 0) {
      return setError("Select at least one permission.");
    }

    const parsedRateCard: Record<string, number> = {};
    for (const [k, v] of Object.entries(rateCard)) {
      if (v.trim() !== "") {
        const n = parseFloat(v);
        if (isNaN(n) || n < 0) return setError(`Invalid rate for ${BEDROOM_LABELS[k]}.`);
        parsedRateCard[k] = n;
      }
    }

    const terms: Record<string, unknown> = {
      fee_structure: { platform_fee_pct: 0 },
      invoicing_cadence: cadence,
    };

    if (myRole === "provider") {
      terms.scope = { apartment_ids: selectedApts };
      terms.permissions = selectedPerms;
    }

    if (Object.keys(parsedRateCard).length > 0) {
      terms.rate_card = parsedRateCard;
    }

    const parsed = serviceEngagementTermsSchema.safeParse(terms);
    if (!parsed.success) {
      return setError(parsed.error.issues[0]?.message ?? "Invalid terms.");
    }

    try {
      await propose.mutateAsync({
        counterpartyOrgId: parseInt(counterpartyOrgId, 10),
        callerRole: myRole,
        contractType: "service_engagement",
        title: title.trim(),
        terms,
        endsAt,
      });
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to propose contract.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Propose Service Engagement Contract</DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {/* Role selector */}
          <div className="space-y-1.5">
            <Label>My role in this contract</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setMyRole("provider")}
                className={`rounded-md border p-3 text-left text-sm transition-colors ${
                  myRole === "provider"
                    ? "border-primary bg-primary/5 font-medium"
                    : "border-border hover:bg-muted/40"
                }`}
              >
                <p className="font-medium">I manage the apartments</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  I grant access to my apartments and define what the service org can do.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setMyRole("consumer")}
                className={`rounded-md border p-3 text-left text-sm transition-colors ${
                  myRole === "consumer"
                    ? "border-primary bg-primary/5 font-medium"
                    : "border-border hover:bg-muted/40"
                }`}
              >
                <p className="font-medium">I provide the service</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  I set my rates. The other org defines which apartments I get access to.
                </p>
              </button>
            </div>
          </div>

          {/* Title */}
          <div className="space-y-1.5">
            <Label>Contract Title</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Cleaning services — Coastal Living 2025"
            />
          </div>

          {/* Counterparty */}
          <div className="space-y-1.5">
            <Label>{myRole === "provider" ? "Service organization" : "Property manager organization"}</Label>
            <Select value={counterpartyOrgId} onValueChange={setCounterpartyOrgId}>
              <SelectTrigger>
                <SelectValue placeholder="Select organization…" />
              </SelectTrigger>
              <SelectContent>
                {orgs.map((org) => (
                  <SelectItem key={org.id} value={String(org.id)}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Provider: apartment scope + permissions */}
          {myRole === "provider" && (
            <>
              <div className="space-y-1.5">
                <Label>Apartments to grant access to</Label>
                {apartments.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No apartments in your account.</p>
                ) : (
                  <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                    {apartments.map((apt) => (
                      <div key={apt.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`apt-${apt.id}`}
                          checked={selectedApts.includes(apt.id)}
                          onCheckedChange={() => toggleApt(apt.id)}
                        />
                        <label htmlFor={`apt-${apt.id}`} className="text-sm cursor-pointer">
                          {apt.name}
                          {apt.bedrooms != null && (
                            <span className="text-muted-foreground ml-1">· {apt.bedrooms} BR</span>
                          )}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Permissions</Label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {Object.keys(PRESETS).map((name) => (
                    <Button key={name} type="button" variant="outline" size="sm"
                      onClick={() => setSelectedPerms(PRESETS[name] ?? [])}>
                      {name}
                    </Button>
                  ))}
                </div>
                <div className="border rounded-md p-3 space-y-3">
                  {PERMISSION_GROUPS.map((group) => (
                    <div key={group.label}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1.5">
                        {group.label}
                      </p>
                      <div className="space-y-1.5 pl-2">
                        {group.permissions.map((p) => (
                          <div key={p} className="flex items-center gap-2">
                            <Checkbox
                              id={`perm-${p}`}
                              checked={selectedPerms.includes(p)}
                              onCheckedChange={() => togglePerm(p)}
                            />
                            <label htmlFor={`perm-${p}`} className="text-sm cursor-pointer font-mono">
                              {p}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Service org: rate card per bedroom */}
          {myRole === "consumer" && (
            <div className="space-y-2">
              <Label>My rates (per cleaning job)</Label>
              <p className="text-xs text-muted-foreground">
                Customer-specific pricing — leave blank to use your account defaults.
              </p>
              <div className="border rounded-md divide-y">
                {Object.keys(BEDROOM_LABELS).map((br) => (
                  <div key={br} className="flex items-center justify-between px-3 py-2 gap-4">
                    <span className="text-sm">{BEDROOM_LABELS[br]}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        className="w-24 h-7 text-sm"
                        value={rateCard[br]}
                        onChange={(e) => setRateCard((prev) => ({ ...prev, [br]: e.target.value }))}
                        placeholder="—"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* End date + cadence */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Contract End Date</Label>
              <Input type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Invoicing Cadence</Label>
              <Select value={cadence} onValueChange={(v) => setCadence(v as typeof cadence)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Biweekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded p-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onClose(); }}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={propose.isPending}>
            {propose.isPending ? "Proposing…" : "Propose Contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── AcceptContractDialog ─────────────────────────────────────────────────────

function AcceptContractDialog({
  contract,
  open,
  onClose,
}: {
  contract: OrgContract;
  open: boolean;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const myOrgId = (user as any)?.organizationId as number | undefined;
  const accept = useAcceptContract();
  const reject = useRejectContract();
  const { data: myApartments = [] } = useApartments();

  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  // Fields for the accepting party to complete their side
  const [selectedApts, setSelectedApts] = useState<number[]>([]);
  const [selectedPerms, setSelectedPerms] = useState<Permission[]>([]);
  const [rateCard, setRateCard] = useState<Record<string, string>>({ "0": "", "1": "", "2": "", "3": "", "4+": "" });

  const existingTerms = contract.terms as Record<string, unknown>;
  const existingScope = existingTerms.scope as { apartment_ids?: number[] } | undefined;
  const existingPermissions = existingTerms.permissions as string[] | undefined;
  const existingRateCard = existingTerms.rate_card as Record<string, number> | undefined;

  // Determine my role: PM org (providerOrgId) = manages apartments; service org (consumerOrgId) = provides service
  const amPmOrg = myOrgId === contract.providerOrgId;
  const amServiceOrg = myOrgId === contract.consumerOrgId;
  const needsScope = amPmOrg && !existingScope?.apartment_ids?.length;
  const needsRateCard = amServiceOrg && !existingRateCard;

  function reset() {
    setShowReject(false);
    setRejectReason("");
    setError(null);
    setSelectedApts([]);
    setSelectedPerms([]);
    setRateCard({ "0": "", "1": "", "2": "", "3": "", "4+": "" });
  }

  async function handleAccept() {
    setError(null);
    if (needsScope) {
      if (selectedApts.length === 0) return setError("Select at least one apartment to grant access to.");
      if (selectedPerms.length === 0) return setError("Select at least one permission.");
    }

    const additionalTerms: Record<string, unknown> = {};
    if (needsScope) {
      additionalTerms.scope = { apartment_ids: selectedApts };
      additionalTerms.permissions = selectedPerms;
    }
    if (needsRateCard) {
      const parsed: Record<string, number> = {};
      for (const [k, v] of Object.entries(rateCard)) {
        if (v.trim() !== "") {
          const n = parseFloat(v);
          if (isNaN(n) || n < 0) return setError(`Invalid rate for ${BEDROOM_LABELS[k]}.`);
          parsed[k] = n;
        }
      }
      if (Object.keys(parsed).length > 0) additionalTerms.rate_card = parsed;
    }

    try {
      await accept.mutateAsync({
        id: contract.id,
        additionalTerms: Object.keys(additionalTerms).length > 0 ? additionalTerms : undefined,
      });
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to accept contract.");
    }
  }

  async function handleReject() {
    setError(null);
    try {
      await reject.mutateAsync({ id: contract.id, reason: rejectReason || undefined });
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to reject contract.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { reset(); onClose(); } }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Review Contract</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div>
            <p className="font-medium text-base">{contract.title}</p>
            <p className="text-muted-foreground">{formatDate(contract.endsAt)}</p>
          </div>

          {/* Existing scope from the proposal */}
          {existingScope?.apartment_ids && existingScope.apartment_ids.length > 0 && (
            <div>
              <p className="font-medium mb-1">Apartments</p>
              <div className="flex flex-wrap gap-1">
                {existingScope.apartment_ids.map((id) => (
                  <Badge key={id} variant="secondary">#{id}</Badge>
                ))}
              </div>
            </div>
          )}
          {existingPermissions && existingPermissions.length > 0 && (
            <div>
              <p className="font-medium mb-1">Permissions</p>
              <div className="flex flex-wrap gap-1">
                {existingPermissions.map((p) => (
                  <Badge key={p} variant="outline" className="font-mono text-xs">{p}</Badge>
                ))}
              </div>
            </div>
          )}
          {existingRateCard && Object.keys(existingRateCard).length > 0 && (
            <div>
              <p className="font-medium mb-1">Service rates (per clean)</p>
              <div className="border rounded-md divide-y">
                {Object.entries(existingRateCard).map(([br, rate]) => (
                  <div key={br} className="flex justify-between px-3 py-1.5">
                    <span>{BEDROOM_LABELS[br] ?? `${br} BR`}</span>
                    <span className="font-medium">${(rate as number).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* PM org completing their side — apartments + permissions */}
          {needsScope && (
            <>
              <div className="border-t pt-3">
                <p className="font-medium mb-2">Your turn: select which apartments to share</p>
                <div className="border rounded-md p-3 max-h-36 overflow-y-auto space-y-2">
                  {myApartments.map((apt) => (
                    <div key={apt.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`accept-apt-${apt.id}`}
                        checked={selectedApts.includes(apt.id)}
                        onCheckedChange={() =>
                          setSelectedApts((p) => p.includes(apt.id) ? p.filter((x) => x !== apt.id) : [...p, apt.id])
                        }
                      />
                      <label htmlFor={`accept-apt-${apt.id}`} className="text-sm cursor-pointer">
                        {apt.name}{apt.bedrooms != null && <span className="text-muted-foreground ml-1">· {apt.bedrooms} BR</span>}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <p className="font-medium">Permissions to grant</p>
                <div className="flex flex-wrap gap-2 mb-1">
                  {Object.keys(PRESETS).map((name) => (
                    <Button key={name} type="button" variant="outline" size="sm"
                      onClick={() => setSelectedPerms(PRESETS[name] ?? [])}>
                      {name}
                    </Button>
                  ))}
                </div>
                <div className="border rounded-md p-3 space-y-3">
                  {PERMISSION_GROUPS.map((group) => (
                    <div key={group.label}>
                      <p className="text-xs font-semibold text-muted-foreground uppercase mb-1">{group.label}</p>
                      <div className="space-y-1.5 pl-2">
                        {group.permissions.map((p) => (
                          <div key={p} className="flex items-center gap-2">
                            <Checkbox id={`accept-perm-${p}`} checked={selectedPerms.includes(p)}
                              onCheckedChange={() =>
                                setSelectedPerms((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p])
                              }
                            />
                            <label htmlFor={`accept-perm-${p}`} className="text-sm cursor-pointer font-mono">{p}</label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Service org completing their side — rates */}
          {needsRateCard && (
            <div className="border-t pt-3 space-y-2">
              <p className="font-medium">Your turn: set your rates for this client</p>
              <div className="border rounded-md divide-y">
                {Object.keys(BEDROOM_LABELS).map((br) => (
                  <div key={br} className="flex items-center justify-between px-3 py-2 gap-4">
                    <span className="text-sm">{BEDROOM_LABELS[br]}</span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm text-muted-foreground">$</span>
                      <Input type="number" min="0" step="0.01" className="w-24 h-7 text-sm"
                        value={rateCard[br]}
                        onChange={(e) => setRateCard((prev) => ({ ...prev, [br]: e.target.value }))}
                        placeholder="—"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {showReject && (
            <div className="space-y-1.5">
              <Label>Rejection reason (optional)</Label>
              <Textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Why are you rejecting this contract?" rows={3} />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded p-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          {!showReject ? (
            <>
              <Button variant="outline" onClick={() => setShowReject(true)} className="text-red-600 border-red-200">
                Reject
              </Button>
              <Button onClick={handleAccept} disabled={accept.isPending}>
                {accept.isPending ? "Accepting…" : "Accept Contract"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => setShowReject(false)}>Back</Button>
              <Button variant="destructive" onClick={handleReject} disabled={reject.isPending}>
                {reject.isPending ? "Rejecting…" : "Confirm Rejection"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── WithdrawButton ───────────────────────────────────────────────────────────

function WithdrawButton({ contractId }: { contractId: number }) {
  const reject = useRejectContract();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleWithdraw() {
    setError(null);
    try {
      await reject.mutateAsync({ id: contractId, reason: "Withdrawn by proposer" });
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to withdraw proposal.");
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        Withdraw Proposal
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Withdraw Proposal</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will cancel the proposal. The other org will be notified.
          </p>
          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded p-2">
              <AlertTriangle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleWithdraw} disabled={reject.isPending}>
              {reject.isPending ? "Withdrawing…" : "Withdraw"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── TerminateButton ──────────────────────────────────────────────────────────

function TerminateButton({ contractId }: { contractId: number }) {
  const terminate = useTerminateContract();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleTerminate() {
    setError(null);
    try {
      await terminate.mutateAsync({ id: contractId, reason: reason || undefined });
      setOpen(false);
      setReason("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to terminate contract.");
    }
  }

  return (
    <>
      <Button variant="destructive" size="sm" onClick={() => setOpen(true)}>
        Terminate Contract
      </Button>

      <Dialog open={open} onOpenChange={(o) => { if (!o) { setReason(""); setError(null); } setOpen(o); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              Terminate Contract
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">
              This will terminate the contract and revoke all active data grants. In-progress
              jobs will complete before access is revoked.
            </p>
            <div className="space-y-1.5">
              <Label>Reason (optional)</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason for termination…"
                rows={3}
              />
            </div>
            {error && (
              <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded p-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleTerminate}
              disabled={terminate.isPending}
            >
              {terminate.isPending ? "Terminating…" : "Terminate"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── ContractDetailsView ──────────────────────────────────────────────────────

function ContractDetailsView({
  contract,
  role,
  onBack,
}: {
  contract: OrgContract;
  role: "provider" | "consumer";
  onBack: () => void;
}) {
  const { data: grants = [], isLoading: grantsLoading } = useOrgDataGrants(contract.id);
  const { data: summary, isLoading: summaryLoading } = useContractSummary(contract.id);
  const { user } = useAuth();
  const [acceptOpen, setAcceptOpen] = useState(false);

  const terms = contract.terms as Record<string, unknown>;
  const scope = terms.scope as { apartment_ids?: number[] } | undefined;
  const permissions = terms.permissions as string[] | undefined;
  const rateCard = terms.rate_card as Record<string, number> | undefined;
  const invoicingCadence = terms.invoicing_cadence as string | undefined;
  const isProposer = user?.id === contract.proposedByUserId;
  const canAccept = contract.status === "proposed" && !isProposer;
  const canWithdraw = contract.status === "proposed" && isProposer;
  const canTerminate = contract.status === "active";

  const counterpartyName = summary?.counterpartyOrg.name ?? (
    role === "consumer" ? `Org #${contract.providerOrgId}` : `Org #${contract.consumerOrgId}`
  );

  // Build apartment name lookup from summary
  const aptNameMap = new Map(summary?.apartments.map((a) => [a.id, a.name]) ?? []);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h2 className="font-semibold text-base">{contract.title}</h2>
            <p className="text-muted-foreground text-xs">
              {contract.contractType} · {statusBadge(contract.status)} ·{" "}
              <Building2 className="w-3 h-3 inline-block mr-0.5" />
              {counterpartyName}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          {canAccept && (
            <Button size="sm" onClick={() => setAcceptOpen(true)}>
              Review & Accept
            </Button>
          )}
          {canWithdraw && <WithdrawButton contractId={contract.id} />}
          {canTerminate && <TerminateButton contractId={contract.id} />}
        </div>
      </div>

      {/* Stats row — only when active and summary loaded */}
      {contract.status === "active" && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Jobs Completed
              </div>
              <p className="text-xl font-semibold">
                {summaryLoading ? "…" : (summary?.stats.jobsCompleted ?? 0)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Clock className="w-3.5 h-3.5" />
                Jobs Pending
              </div>
              <p className="text-xl font-semibold">
                {summaryLoading ? "…" : (summary?.stats.jobsPending ?? 0)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <DollarSign className="w-3.5 h-3.5" />
                {role === "consumer" ? "Revenue Earned" : "Total Billed"}
              </div>
              <p className="text-xl font-semibold">
                {summaryLoading ? "…" : `$${parseFloat(summary?.stats.totalEarned ?? "0").toFixed(2)}`}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <FileText className="w-3.5 h-3.5" />
                Outstanding
              </div>
              <p className="text-xl font-semibold">
                {summaryLoading ? "…" : `$${parseFloat(summary?.stats.totalOutstanding ?? "0").toFixed(2)}`}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabbed detail */}
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          {contract.status === "active" && (
            <>
              <TabsTrigger value="jobs">
                Jobs {summary ? `(${summary.stats.jobsCompleted + summary.stats.jobsPending})` : ""}
              </TabsTrigger>
              <TabsTrigger value="invoices">
                Invoices {summary ? `(${summary.invoices.length})` : ""}
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* ── Overview tab ── */}
        <TabsContent value="overview" className="space-y-4 mt-4">
          {/* Terms summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Contract Terms</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-medium text-muted-foreground text-xs uppercase mb-1">Ends At</p>
                  <p>{formatDate(contract.endsAt)}</p>
                </div>
                <div>
                  <p className="font-medium text-muted-foreground text-xs uppercase mb-1">Invoicing</p>
                  <p>{invoicingCadence ?? "—"}</p>
                </div>
              </div>


              {scope?.apartment_ids && scope.apartment_ids.length > 0 && (
                <div>
                  <p className="font-medium text-muted-foreground text-xs uppercase mb-1">
                    Apartment Scope ({scope.apartment_ids.length})
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {scope.apartment_ids.map((id) => (
                      <Badge key={id} variant="secondary">
                        {aptNameMap.get(id) ?? `#${id}`}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {permissions && permissions.length > 0 && (
                <div>
                  <p className="font-medium text-muted-foreground text-xs uppercase mb-1">
                    Permissions
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {permissions.map((p) => (
                      <Badge key={p} variant="outline" className="font-mono text-xs">{p}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {rateCard && Object.keys(rateCard).length > 0 && (
                <div>
                  <p className="font-medium text-muted-foreground text-xs uppercase mb-1">
                    Service Rates (per clean)
                  </p>
                  <div className="border rounded-md divide-y text-sm">
                    {Object.entries(rateCard).map(([br, rate]) => (
                      <div key={br} className="flex justify-between px-3 py-1.5">
                        <span>{BEDROOM_LABELS[br] ?? `${br} BR`}</span>
                        <span className="font-medium">${rate.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Grants */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Data Grants</CardTitle>
            </CardHeader>
            <CardContent>
              {grantsLoading ? (
                <p className="text-muted-foreground text-sm">Loading grants…</p>
              ) : grants.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">
                  No grants yet. Grants are created when this contract is accepted.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 pr-4 font-medium">Resource</th>
                        <th className="text-left py-2 pr-4 font-medium">Permissions</th>
                        <th className="text-left py-2 pr-4 font-medium">Expires At</th>
                        <th className="text-left py-2 font-medium">Revoked At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grants.map((g) => (
                        <tr key={g.id} className="border-b">
                          <td className="py-2 pr-4">
                            {g.resourceType} #{g.resourceId}
                          </td>
                          <td className="py-2 pr-4">
                            <div className="flex flex-wrap gap-1">
                              {(g.permissions as string[]).map((p) => (
                                <Badge key={p} variant="secondary" className="text-xs">
                                  {p}
                                </Badge>
                              ))}
                            </div>
                          </td>
                          <td className="py-2 pr-4">{formatDate(g.expiresAt)}</td>
                          <td className="py-2">
                            {g.revokedAt ? (
                              <span className="text-red-600">{formatDate(g.revokedAt)}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Jobs tab ── */}
        <TabsContent value="jobs" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Recent Jobs (last 50)</CardTitle>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
              ) : !summary || summary.recentJobs.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">No jobs yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 pr-4 font-medium">Apartment</th>
                        <th className="text-left py-2 pr-4 font-medium">Status</th>
                        <th className="text-left py-2 pr-4 font-medium">Scheduled</th>
                        <th className="text-left py-2 pr-4 font-medium">Completed</th>
                        <th className="text-left py-2 font-medium">Assignee</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.recentJobs.map((j) => (
                        <tr key={j.id} className="border-b">
                          <td className="py-2 pr-4">{j.apartmentName}</td>
                          <td className="py-2 pr-4">{statusBadge(j.status)}</td>
                          <td className="py-2 pr-4">{j.scheduledDate ? formatDate(j.scheduledDate) : "—"}</td>
                          <td className="py-2 pr-4">{j.completedAt ? formatDate(j.completedAt) : "—"}</td>
                          <td className="py-2">{j.assigneeName ?? <span className="text-muted-foreground">Unassigned</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Invoices tab ── */}
        <TabsContent value="invoices" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              {summaryLoading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
              ) : !summary || summary.invoices.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4 text-center">No invoices yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-muted-foreground">
                        <th className="text-left py-2 pr-4 font-medium">Invoice #</th>
                        <th className="text-left py-2 pr-4 font-medium">Period</th>
                        <th className="text-left py-2 pr-4 font-medium">Status</th>
                        <th className="text-right py-2 pr-4 font-medium">Subtotal</th>
                        <th className="text-right py-2 font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.invoices.map((inv) => (
                        <tr key={inv.id} className="border-b">
                          <td className="py-2 pr-4 font-mono text-xs">{inv.invoiceNumber}</td>
                          <td className="py-2 pr-4">
                            {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                          </td>
                          <td className="py-2 pr-4">{statusBadge(inv.status)}</td>
                          <td className="py-2 pr-4 text-right">${parseFloat(inv.subtotal).toFixed(2)}</td>
                          <td className="py-2 text-right font-medium">${parseFloat(inv.total).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {canAccept && (
        <AcceptContractDialog
          contract={contract}
          open={acceptOpen}
          onClose={() => setAcceptOpen(false)}
        />
      )}
    </div>
  );
}

// ─── ContractTable ────────────────────────────────────────────────────────────

function ContractTable({
  contracts,
  role,
  onSelect,
}: {
  contracts: OrgContract[];
  role: "provider" | "consumer";
  onSelect: (c: OrgContract) => void;
}) {
  if (contracts.length === 0) {
    return (
      <p className="text-muted-foreground text-sm py-6 text-center">No contracts yet.</p>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b text-muted-foreground">
            <th className="text-left py-2 pr-4 font-medium">Title</th>
            <th className="text-left py-2 pr-4 font-medium">Type</th>
            <th className="text-left py-2 pr-4 font-medium">Status</th>
            <th className="text-left py-2 pr-4 font-medium">
              {role === "provider" ? "Service Org" : "Property Manager"}
            </th>
            <th className="text-left py-2 font-medium">Ends At</th>
          </tr>
        </thead>
        <tbody>
          {contracts.map((c) => (
            <tr
              key={c.id}
              className="border-b hover:bg-muted/40 cursor-pointer transition-colors"
              onClick={() => onSelect(c)}
            >
              <td className="py-2 pr-4 font-medium">{c.title}</td>
              <td className="py-2 pr-4">{c.contractType}</td>
              <td className="py-2 pr-4">{statusBadge(c.status)}</td>
              <td className="py-2 pr-4 text-muted-foreground">
                {role === "provider" ? c.consumerOrgId : c.providerOrgId}
              </td>
              <td className="py-2">{formatDate(c.endsAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminCrossOrg() {
  const { data, isLoading } = useOrgContracts();
  const [selectedContract, setSelectedContract] = useState<OrgContract | null>(null);
  const [selectedRole, setSelectedRole] = useState<"provider" | "consumer">("provider");
  const [proposeOpen, setProposeOpen] = useState(false);

  const asProvider = data?.asProvider ?? [];
  const asConsumer = data?.asConsumer ?? [];

  function selectContract(c: OrgContract, role: "provider" | "consumer") {
    setSelectedContract(c);
    setSelectedRole(role);
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Cross-Org Contracts</h1>
          <p className="text-muted-foreground">
            Inter-organization service agreements and data grants
          </p>
        </div>

        {selectedContract ? (
          <ContractDetailsView
            contract={selectedContract}
            role={selectedRole}
            onBack={() => setSelectedContract(null)}
          />
        ) : isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : (
          <Card>
            <CardContent className="pt-4">
              <Tabs defaultValue="service">
                <div className="flex items-center justify-between mb-4">
                  <TabsList>
                    <TabsTrigger value="service" data-testid="tab-as-service">
                      As Service Provider ({asConsumer.length})
                    </TabsTrigger>
                    <TabsTrigger value="pm" data-testid="tab-as-pm">
                      As Property Manager ({asProvider.length})
                    </TabsTrigger>
                  </TabsList>
                  <Button size="sm" onClick={() => setProposeOpen(true)}>
                    <Plus className="w-4 h-4 mr-1" />
                    Propose Contract
                  </Button>
                </div>

                {/* asConsumer = contracts where I'm the service org */}
                <TabsContent value="service">
                  <ContractTable
                    contracts={asConsumer}
                    role="consumer"
                    onSelect={(c) => selectContract(c, "consumer")}
                  />
                </TabsContent>

                {/* asProvider = contracts where I'm the PM org (has apartments) */}
                <TabsContent value="pm">
                  <ContractTable
                    contracts={asProvider}
                    role="provider"
                    onSelect={(c) => selectContract(c, "provider")}
                  />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}

        <ProposeContractDialog open={proposeOpen} onClose={() => setProposeOpen(false)} />
      </div>
    </AdminLayout>
  );
}
